import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

import { WorldStagePass } from './passes/WorldStagePass.js';
import { ViewmodelPass } from './passes/ViewmodelPass.js';
import { LensShader } from './passes/LensShader.js';
import { GradeShader } from './passes/GradeShader.js';

/**
 * The post chain.
 *
 * Graph, in order:
 *
 *   [WorldStagePass]  world -> HDR target (+depth)  ->  GTAO  ->  camera motion blur
 *   [ViewmodelPass]   depth cleared, gun drawn over the top
 *   [UnrealBloomPass] tight, warm, high threshold
 *   [LensPass]        distortion + chromatic aberration + ADS defocus + flash   (HDR)
 *   [OutputPass]      ACES tone map + sRGB encode                               (the only tone map)
 *   [SMAAPass]        edge anti-aliasing, before anything adds noise
 *   [GradePass]       ASC-CDL + split tone + grain + vignette + damage  -> screen
 *
 * The depth-dependent effects deliberately live *before* the viewmodel is
 * composited: run AO after and the gun picks up a black halo where it overlaps
 * the world, run motion blur after and the gun smears while the camera turns
 * even though it is bolted to the camera.
 *
 * The grade lands after the tone map because slope/offset/power is defined on
 * code values, not on scene linear light. `renderer.toneMapping` is already
 * ACESFilmic and OutputPass consumes it — nothing else in here touches the tone
 * curve.
 *
 * Design brief for the numbers below: the whole stack should be invisible.
 * Toggling it off should make the image look *worse* without anyone being able
 * to point at which effect went missing.
 */

export const TUNING = {
  /* ---- ambient occlusion (metre-scale geometry) ---- */
  ao: {
    radius: 0.55, // metres — contact shading under props and in corners, not a room-wide dim
    distanceExponent: 1.7,
    thickness: 0.35,
    distanceFallOff: 1.0,
    scale: 1.0,
    samples: 16,
    screenSpaceRadius: false,
    intensity: 0.85,
    resolutionScale: 0.5,
  },
  aoDenoise: { lumaPhi: 10, depthPhi: 2, normalPhi: 3, radius: 4, rings: 2, samples: 8 },

  /* ---- motion blur ---- */
  motionBlur: {
    strength: 0.55, // ~180 degree shutter
    maxPixels: 11,
  },

  /* ---- bloom: sun, muzzle flash, hot speculars. Nothing else. ---- */
  bloom: {
    strength: 0.30,
    radius: 0.22,
    threshold: 1.70,
    // Warmer the further out the mip, the way a real coated lens flares.
    tint: [
      [1.0, 0.985, 0.955],
      [1.0, 0.968, 0.910],
      [1.0, 0.945, 0.860],
      [1.0, 0.922, 0.815],
      [1.0, 0.900, 0.775],
    ],
  },

  /* ---- lens ---- */
  lens: {
    distortion: 0.030,
    // uv offset per channel at the very corner. 0.0008 * 0.707 ≈ 1.8 px at 4K,
    // under one CSS pixel — the level where it reads as glass rather than as a
    // video filter. Anything near 0.003 puts visible rainbow on every wire.
    chroma: 0.0008,
    adsRadius: 0.010,
  },

  /* ---- grade ---- */
  grade: {
    slope: [1.035, 1.015, 0.995],
    offset: [-0.008, -0.005, 0.004],
    power: [1.045, 1.030, 0.995],
    saturation: 1.025,
    shadowDesat: 0.22, // chroma falls off in the toe, the way film does
    shadowTint: [-0.006, 0.003, 0.012], // cool teal in the toe
    highTint: [0.018, 0.007, -0.010], // warm in the shoulder
    // Slight dye cross-talk. Rows must each sum to ~1 or the neutral axis shifts.
    mixer: [
      [1.020, -0.014, -0.006],
      [-0.010, 1.014, -0.004],
      [-0.004, -0.016, 1.020],
    ],
    vignette: 0.24,
    grain: 0.030,
  },

  /* ---- gameplay overlays ---- */
  damageAttack: 14.0, // 1/s toward a raised target
  damageRelease: 4.5,
  damageDecay: 1.15, // auto bleed-off when nobody is driving it
  adsLerp: 12.0,
};

/** Fullscreen quads must never read or write depth. */
function flattenDepthState(...materials) {
  for (const m of materials) {
    if (!m) continue;
    m.depthTest = false;
    m.depthWrite = false;
  }
}

export class PostFX {
  constructor(ctx) {
    this.ctx = ctx;
    const { renderer, scene, camera, viewScene, viewCamera, quality } = ctx;

    const size = renderer.getSize(new THREE.Vector2());
    const pr = renderer.getPixelRatio();
    this._w = Math.max(1, Math.floor(size.width * pr));
    this._h = Math.max(1, Math.floor(size.height * pr));

    const useAO = !!quality.ssao;
    const useMotionBlur = !!quality.motionBlur;

    /* ------------------------------------------------------------------ */
    /* composer                                                            */
    /* ------------------------------------------------------------------ */
    const hdrTarget = new THREE.WebGLRenderTarget(this._w, this._h, {
      type: THREE.HalfFloatType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
      stencilBuffer: false,
    });
    hdrTarget.texture.name = 'PostFX.hdr';

    this.composer = new EffectComposer(renderer, hdrTarget);

    /* ---- 1/2. world + depth-dependent effects ------------------------- */
    this.worldPass = new WorldStagePass(scene, camera, {
      ao: useAO,
      motionBlur: useMotionBlur,
      aoScale: TUNING.ao.resolutionScale,
      aoParams: TUNING.ao,
      pdParams: TUNING.aoDenoise,
    });
    this.worldPass.aoIntensity = TUNING.ao.intensity;
    this.worldPass.motionBlurStrength = TUNING.motionBlur.strength;
    this.worldPass.setMaxBlurPixels(TUNING.motionBlur.maxPixels);
    this.composer.addPass(this.worldPass);

    /* ---- 3. viewmodel over the top ------------------------------------ */
    this.viewmodelPass = new ViewmodelPass(viewScene, viewCamera);
    this.composer.addPass(this.viewmodelPass);

    /* ---- 4. bloom ------------------------------------------------------ */
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(this._w, this._h),
      TUNING.bloom.strength,
      TUNING.bloom.radius,
      TUNING.bloom.threshold,
    );
    TUNING.bloom.tint.forEach((t, i) => this.bloomPass.bloomTintColors[i].set(t[0], t[1], t[2]));
    this.composer.addPass(this.bloomPass);

    /* ---- 5. lens (HDR) -------------------------------------------------- */
    this.lensPass = new ShaderPass(LensShader);
    {
      const u = this.lensPass.uniforms;
      u.uDistortion.value = TUNING.lens.distortion;
      u.uChroma.value = TUNING.lens.chroma;
      u.uAdsRadius.value = TUNING.lens.adsRadius;
      u.uAspect.value = this._w / this._h;
    }
    this.composer.addPass(this.lensPass);

    /* ---- 6. tone map + encode ------------------------------------------ */
    this.outputPass = new OutputPass();
    this.composer.addPass(this.outputPass);

    /* ---- 7. SMAA -------------------------------------------------------- */
    // AA runs on the tone-mapped image and, crucially, *before* the grain.
    // SMAA classifies edges from local luma contrast; feeding it a grained
    // image makes every flat surface look like an edge, so its blend weights go
    // to noise and the pass stops removing stair-steps. Grain is a property of
    // the print and belongs after the geometry has been resolved.
    this.smaaPass = new SMAAPass(this._w, this._h);
    // Engine runs with renderer.autoClear = false and the SMAA edge shader
    // discards non-edge pixels — without an explicit clear, last frame's edges
    // stay in the buffer and ghost.
    this.smaaPass.clear = true;
    this.composer.addPass(this.smaaPass);

    /* ---- 8. grade (display space, last) --------------------------------- */
    this.gradePass = new ShaderPass(GradeShader);
    this._applyGrade(TUNING.grade);
    this.composer.addPass(this.gradePass);

    /* ------------------------------------------------------------------ */
    /* two housekeeping fixes for the fact that Engine runs the renderer    */
    /* with autoClear = false                                              */
    /* ------------------------------------------------------------------ */

    // three's fullscreen-quad passes assume autoClear will wipe the depth
    // buffer for them. With autoClear off, the quad (which sits at depth 0 and
    // writes depth by default) passes the depth test on the first frame,
    // leaves 0 behind, and then fails LESS against itself forever after —
    // giving a chain that renders once and then goes black. Fullscreen quads
    // have no business touching depth at all, so take them out of the game.
    flattenDepthState(
      this.lensPass.material,
      this.gradePass.material,
      this.outputPass.material,
      this.smaaPass.materialEdges,
      this.smaaPass.materialWeights,
      this.smaaPass.materialBlend,
      this.bloomPass.materialHighPassFilter,
      this.bloomPass.compositeMaterial,
      this.bloomPass.blendMaterial,
      this.bloomPass.basic,
      this.composer.copyPass.material,
      ...this.bloomPass.separableBlurMaterials,
    );

    // Keep the lens aspect and the motion-blur pixel clamp honest across resizes.
    this.lensPass.setSize = (w, h) => {
      this.lensPass.uniforms.uAspect.value = w / h;
    };

    // EffectComposer caches the pixel ratio at construction; Engine.resize()
    // re-reads devicePixelRatio (monitor changes) before calling us.
    const composer = this.composer;
    const baseSetSize = composer.setSize.bind(composer);
    composer.setSize = (w, h) => {
      composer._pixelRatio = renderer.getPixelRatio();
      baseSetSize(w, h);
    };

    this.composer.setSize(size.width, size.height);

    /* ------------------------------------------------------------------ */
    /* gameplay-driven state                                               */
    /* ------------------------------------------------------------------ */
    this._damage = 0;
    this._damageTarget = 0;
    this._damageDriven = false;
    this._ads = 0;
    this._adsTarget = 0;
    this._flash = { value: 0, peak: 0, t: 0, duration: 1 };

    this._unregister = ctx.engine.registerUpdate((dt) => this._update(dt), 'ui');

    // The lead hooks events to this; also a handy console handle in dev.
    ctx.postfx = this;
    if (typeof window !== 'undefined') window.__postfx = this;
  }

  /* -------------------------------------------------------------------- */
  /* public API                                                            */
  /* -------------------------------------------------------------------- */

  /**
   * Player damage overlay: desaturates the frame and pushes a red-fringed
   * vignette in from the edges. Widening the chromatic aberration at the same
   * time is what sells it as an eye response rather than a red PNG.
   *
   * Drive it per-frame from health, or fire it once on a hit — if nothing sets
   * it again it bleeds off on its own.
   * @param {number} v 0..1
   */
  setDamageIntensity(v) {
    this._damageTarget = THREE.MathUtils.clamp(v, 0, 1);
    this._damageDriven = true;
  }

  /**
   * Full-screen white flash for flashbangs and close explosions. Added in HDR,
   * so the tone curve clips it the way a real sensor would.
   * @param {number} intensity 0..1+
   * @param {number} duration seconds
   */
  flash(intensity = 1, duration = 0.4) {
    const f = this._flash;
    // A second bang mid-flash should re-arm, never darken.
    f.peak = Math.max(intensity, f.value);
    f.duration = Math.max(0.016, duration);
    f.t = 0;
    f.value = f.peak;
  }

  /**
   * Peripheral defocus while aiming down sights: the centre stays razor sharp,
   * the outer quarter of the frame goes soft. 0 = hip fire, 1 = fully aimed.
   * @param {number} v 0..1
   */
  setAdsBlur(v) {
    this._adsTarget = THREE.MathUtils.clamp(v, 0, 1);
  }

  /** Runtime quality toggles (pause menu). */
  setAmbientOcclusion(on) {
    this.worldPass.aoEnabled = !!on && this.worldPass.gtao !== null;
  }

  setMotionBlur(on) {
    this.worldPass.motionBlurEnabled = !!on;
  }

  setBloomEnabled(on) {
    this.bloomPass.enabled = !!on;
  }

  setAntialiasEnabled(on) {
    this.smaaPass.enabled = !!on;
  }

  /* -------------------------------------------------------------------- */
  /* internals                                                             */
  /* -------------------------------------------------------------------- */

  _applyGrade(g) {
    const u = this.gradePass.uniforms;
    u.uSlope.value.fromArray(g.slope);
    u.uOffset.value.fromArray(g.offset);
    u.uPower.value.fromArray(g.power);
    u.uSaturation.value = g.saturation;
    u.uShadowDesat.value = g.shadowDesat;
    u.uShadowTint.value.fromArray(g.shadowTint);
    u.uHighTint.value.fromArray(g.highTint);
    u.uVignette.value = g.vignette;
    u.uGrain.value = g.grain;
    const m = g.mixer;
    u.uMixer.value.set(
      m[0][0], m[0][1], m[0][2],
      m[1][0], m[1][1], m[1][2],
      m[2][0], m[2][1], m[2][2],
    );
  }

  _update(dt) {
    /* ---- damage ---- */
    if (!this._damageDriven) {
      this._damageTarget = Math.max(0, this._damageTarget - TUNING.damageDecay * dt);
    }
    this._damageDriven = false;
    const rate = this._damageTarget > this._damage ? TUNING.damageAttack : TUNING.damageRelease;
    this._damage += (this._damageTarget - this._damage) * Math.min(1, rate * dt);

    /* ---- ads ---- */
    this._ads += (this._adsTarget - this._ads) * Math.min(1, TUNING.adsLerp * dt);

    /* ---- flash ---- */
    const f = this._flash;
    if (f.value > 0) {
      f.t += dt;
      const p = f.t / f.duration;
      f.value = p >= 1 ? 0 : f.peak * Math.pow(1 - p, 2.2);
    }

    const lu = this.lensPass.uniforms;
    lu.uDamage.value = this._damage;
    lu.uAds.value = this._ads;
    lu.uFlash.value = f.value;

    const gu = this.gradePass.uniforms;
    gu.uDamage.value = this._damage;
    // A fresh spatial offset every frame: real film grain, no temporal pattern,
    // and no float-precision drift from feeding a growing clock into the hash.
    gu.uGrainSeed.value.set(Math.random() * 1024, Math.random() * 1024);
  }

  setSize(width, height) {
    this.composer.setSize(width, height);
  }

  dispose() {
    this._unregister?.();
    this.worldPass.dispose();
    this.bloomPass.dispose();
    this.lensPass.dispose?.();
    this.gradePass.dispose?.();
    this.smaaPass.dispose?.();
    this.outputPass.dispose?.();
    this.composer.renderTarget1.dispose();
    this.composer.renderTarget2.dispose();
    if (this.ctx.postfx === this) this.ctx.postfx = null;
  }
}
